import { Text } from '@/components/ui/Text'
import { CreativeLibraryTab } from './creatives/CreativeLibraryTab'
import type { CreativeLibraryView } from '@/types/creative-library'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  KnowledgeFacetAggregate,
  KnowledgeSearchHit,
  LibraryCollectionPreset,
  LibraryCollectionRules,
  LibraryItemRef,
  LibrarySearchBrand,
  SocialUniverseItem,
} from '@digital-twins/shared-types'
import { CollectionCreateDialog } from '@/components/library/CollectionCreateDialog'
import { CollectionShareDialog } from '@/components/library/CollectionShareDialog'
import { CollectionsRail } from '@/components/library/CollectionsRail'
import type { CollectionWizardEntry } from '@/types/components/library/collectionWizardAnalytics'
import { LibrarySelectionBar } from '@/components/library/LibrarySelectionBar'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { PageContent } from '@/components/layout/PageContent'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { useAgentHandlerEnabled } from '@/hooks/useAgentHandlerEnabled'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useCollectionShareAdapter } from '@/hooks/useCollectionShareAdapter'
import { useAuth } from '@/lib/config/auth-provider'
import { PYLON_CONTACT_SUPPORT_URL } from '@/lib/constants/pylon'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { knowledgeApi, KnowledgeDocumentWithStages } from '@/lib/api'
import AudiencesPage from '@/pages/AudiencesPage/AudiencesPage'
import { onKnowledgeLinkIngestUpdated } from '@/lib/services/websocket'
import { ROUTES } from '@/routes/routes'
import { applyKnowledgeIndexingEventToDocuments } from './applyKnowledgeIndexingEvent'
import { useKnowledgeDocumentIndexingUpdates } from './useKnowledgeDocumentIndexingUpdates'
import { KnowledgeDocumentCollection } from './KnowledgeDocumentCollection'
import { KnowledgeLibrarySearch } from './KnowledgeLibrarySearch'
import { KnowledgeLibraryBand } from './KnowledgeLibraryBand'
import { KnowledgeSearchResults } from './KnowledgeSearchResults'
import {
  documentMatchesFacetFilters,
  documentTypeLabel,
  filtersFromSearchParams,
  isQuestionShaped,
} from './libraryHelpers'
import { KnowledgeAddLinksModal } from './KnowledgeAddLinksModal'
import { KnowledgeDocumentModal } from '../KnowledgeDocumentPage/KnowledgeDocumentModal'
import { KnowledgeLibraryHeader } from './KnowledgeLibraryHeader'
import { LibrarySources } from './LibrarySources'
import { SaveFiltersInput, SaveFiltersPopover } from './SaveFiltersPopover'
import { SocialMediaTab } from './social/SocialMediaTab'
import { SocialUniverseDialog } from './social/SocialUniverseDialog'
import { useSocialUniverses } from './social/useSocialUniverses'
import { useSocialTopicListFilters } from './social/useSocialTopicListFilters'
import { SocialTopicListToolbar } from './social/SocialTopicListToolbar'
import { LibraryCollectionsGrid } from './collections/LibraryCollectionsGrid'
import { LibraryFeaturedBanner, pickFeaturedCollection } from './collections/LibraryFeaturedBanner'
import {
  LibraryGroupedSearchResults,
  brandRulesFromSearchBrand,
} from './collections/LibraryGroupedSearchResults'
import { useLibrarySearch } from './collections/useLibrarySearch'
import { useLibraryCollectionsList } from '../KnowledgeCollectionsListPage/useLibraryCollectionsList'

const LIBRARY_VIEW_PARAM = 'view'
const LIBRARY_SOCIAL_VIEW = 'social'
const LIBRARY_CONNECTORS_VIEW = 'connectors'
const LIBRARY_AUDIENCES_VIEW = 'audiences'
const AUDIENCE_TAB_PARAMS = ['audienceType', 'categories', 'countries', 'audience'] as const
/** Links from before the Topic page (`?universe=`) go to it. */
const SOCIAL_UNIVERSE_PARAM = 'universe'
/** Links from before Topics go to the page of the Topic that owns the channel. */
const SOCIAL_TRACKER_PARAM = 'tracker'
/** Typing settles this long before the grouped search reads (the document search's debounce). */
const SEARCH_DEBOUNCE_MS = 250

interface CollectionDialogState {
  entry: CollectionWizardEntry
  initialPreset?: LibraryCollectionPreset
  initialRules?: Partial<LibraryCollectionRules>
}

/** The query the Library searches for, or '' when it should not search (too short, or a question for Ask). */
function searchableQuery(query: string): string {
  const q = query.trim()
  return q.length >= 2 && !isQuestionShaped(q) ? q : ''
}

export default function KnowledgeHubPage() {
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const connectorsEnabled = useAgentHandlerEnabled()
  const { isLoading: isAuthLoading, user } = useAuth()
  const canLoadLibrary = !isAuthLoading && assistantPlatformEnabled
  const isLibraryLocked = !isAuthLoading && !assistantPlatformEnabled
  const socialUniverses = useSocialUniverses(canLoadLibrary)
  const socialList = useSocialTopicListFilters(socialUniverses.universes, socialUniverses.schedule)
  const matchingUniverses = socialList.result
  const isSocialEnabled = canLoadLibrary && socialUniverses.isEnabled
  const libraryView = searchParams.get(LIBRARY_VIEW_PARAM)
  const isSocialView = isSocialEnabled && libraryView === LIBRARY_SOCIAL_VIEW
  const isAudiencesView = canLoadLibrary && libraryView === LIBRARY_AUDIENCES_VIEW
  const isConnectorsView = connectorsEnabled && libraryView === LIBRARY_CONNECTORS_VIEW
  const creativeView: CreativeLibraryView | null = canLoadLibrary && (libraryView === 'brands' || libraryView === 'boards' || libraryView === 'creatives') ? (libraryView === 'boards' ? 'creatives' : libraryView) : null
  const activeLibraryView = creativeView ?? (isConnectorsView ? 'connectors' : isAudiencesView ? 'audiences' : isSocialView ? 'social' : 'docs')
  const isResourcesView = !isSocialView && !isAudiencesView && !isConnectorsView && !creativeView
  const [documents, setDocuments] = useState<
    Array<KnowledgeDocumentWithStages>
  >([])
  const [facetCatalog, setFacetCatalog] = useState<
    Array<KnowledgeFacetAggregate>
  >([])
  const [loading, setLoading] = useState(true)
  const [deletingToken, setDeletingToken] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchHits, setSearchHits] = useState<Array<KnowledgeSearchHit>>([])
  const [searching, setSearching] = useState(false)
  const [activeType, setActiveType] = useState<string | null>(null)
  const [filters, setFilters] = useState<Record<string, string | null>>({})
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [addLinksOpen, setAddLinksOpen] = useState(false)
  const [isTopicDialogOpen, setIsTopicDialogOpen] = useState(false)
  const [editingUniverse, setEditingUniverse] =
    useState<SocialUniverseItem | null>(null)
  const [openDocToken, setOpenDocToken] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window === 'undefined') return 'grid'
    return window.sessionStorage.getItem('knowledge-library-view') === 'list'
      ? 'list'
      : 'grid'
  })
  const [groupedQuery, setGroupedQuery] = useState('')
  const [collectionDialog, setCollectionDialog] =
    useState<CollectionDialogState | null>(null)
  // A saved filter just made for Specific people: its share dialog picks them.
  const [shareCollectionToken, setShareCollectionToken] = useState<string | null>(null)
  // The Resources multi-select (AF7 owns it, AF8 draws the checkboxes and the bar):
  // held here so it survives the grid and list switch; cleared on a tab change.
  const [selectedItems, setSelectedItems] = useState<Array<LibraryItemRef>>([])
  const loadGenerationRef = useRef(0)
  const collectionsList = useLibraryCollectionsList(canLoadLibrary)
  const librarySearch = useLibrarySearch(groupedQuery)
  const shareAdapter = useCollectionShareAdapter(
    shareCollectionToken,
    user?.organizationToken,
  )

  async function loadLibrary(options?: { quiet?: boolean }) {
    const generation = ++loadGenerationRef.current
    if (!options?.quiet) {
      setLoading(true)
    }
    try {
      const [list, facetsResponse] = await Promise.all([
        knowledgeApi.listDocuments(),
        knowledgeApi
          .listFacets()
          .catch(() => ({ facets: [], documentCount: 0 })),
      ])
      if (generation !== loadGenerationRef.current) {
        return
      }
      setDocuments(list)
      setFacetCatalog(facetsResponse.facets)
    } catch (error: unknown) {
      if (generation !== loadGenerationRef.current) {
        return
      }
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('library.loadFailed')
      toast.error(message)
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  function handleDocumentUpdated(updated: KnowledgeDocumentWithStages) {
    setDocuments((prev) =>
      prev.map((doc) => (doc.token === updated.token ? updated : doc)),
    )
  }

  useKnowledgeDocumentIndexingUpdates(
    (event) => {
      setDocuments((prev) => {
        const result = applyKnowledgeIndexingEventToDocuments(prev, event)
        if (result.missingDocument) {
          void loadLibrary({ quiet: true })
          return prev
        }
        return result.documents
      })
    },
    { enabled: canLoadLibrary },
  )

  useEffect(() => {
    if (!canLoadLibrary) {
      return
    }
    return onKnowledgeLinkIngestUpdated((event) => {
      if (event.status === 'failed') {
        void loadLibrary({ quiet: true })
        return
      }
      if (event.status === 'created') {
        void loadLibrary({ quiet: true })
      }
    })
  }, [canLoadLibrary])

  useEffect(() => {
    window.sessionStorage.setItem('knowledge-library-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    if (!canLoadLibrary) {
      return
    }
    void loadLibrary()
  }, [canLoadLibrary])

  // Deep-link: /knowledge?ask=1&q=...&doc=token → Threads
  useEffect(() => {
    const ask = searchParams.get('ask')
    const q = searchParams.get('q')
    const doc = searchParams.get('doc')
    const fromUrl = filtersFromSearchParams(searchParams)
    const onAudiencesTab = searchParams.get(LIBRARY_VIEW_PARAM) === LIBRARY_AUDIENCES_VIEW

    if (!onAudiencesTab && Object.keys(fromUrl).length > 0) {
      setFilters((prev) => ({ ...prev, ...fromUrl }))
      if (fromUrl.type) {
        setActiveType(fromUrl.type)
      }
    }
    if (q && ask !== '1' && ask !== 'true' && !doc) {
      setSearchQuery(q)
    }
    if (ask === '1' || ask === 'true') {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (doc) params.set('doc', doc)
      const qs = params.toString()
      navigate(qs ? `/threads?${qs}` : '/threads', { replace: true })
      return
    }
    if (doc) {
      setOpenDocToken(doc)
    }
  }, [searchParams, navigate])

  useEffect(() => {
    if (!canLoadLibrary || !isResourcesView) {
      setSearchHits([])
      setSearching(false)
      return
    }
    const q = searchQuery.trim()
    if (q.length < 2 || isQuestionShaped(q)) {
      setSearchHits([])
      setSearching(false)
      return
    }
    setSearching(true)
    let cancelled = false
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await knowledgeApi.search(q)
          if (!cancelled) {
            setSearchHits(result.hits)
          }
        } catch {
          if (!cancelled) {
            setSearchHits([])
          }
        } finally {
          if (!cancelled) setSearching(false)
        }
      })()
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [searchQuery, canLoadLibrary, isResourcesView])

  useEffect(() => {
    if (!isResourcesView) {
      return
    }
    const q = canLoadLibrary ? searchableQuery(searchQuery) : ''
    if (!q) {
      setGroupedQuery('')
      return
    }
    const handle = window.setTimeout(() => setGroupedQuery(q), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [searchQuery, canLoadLibrary, isResourcesView])

  const selectedTokens = useMemo(
    () => new Set(selectedItems.map((item) => item.token)),
    [selectedItems],
  )

  function toggleSelected(token: string) {
    setSelectedItems((prev) =>
      prev.some((item) => item.token === token)
        ? prev.filter((item) => item.token !== token)
        : [...prev, { kind: 'document', token }],
    )
  }

  function dropFromSelection(token: string) {
    setSelectedItems((prev) =>
      prev.some((item) => item.token === token)
        ? prev.filter((item) => item.token !== token)
        : prev,
    )
  }

  function handleOpenBrand(brand: LibrarySearchBrand) {
    if (brand.collectionToken) {
      navigate(ROUTES.knowledgeCollection(brand.collectionToken))
      return
    }
    setCollectionDialog({
      entry: 'brand_search',
      initialPreset: 'brand',
      initialRules: brandRulesFromSearchBrand(brand),
    })
  }

  function handleCollectionSaved(token: string) {
    setCollectionDialog(null)
    navigate(ROUTES.knowledgeCollection(token))
  }

  async function handleSaveFilters(input: SaveFiltersInput) {
    const detail = await collectionsList.create({
      name: input.name,
      preset: 'custom',
      rules: input.rules,
      origin: 'saved_filter',
      sharingMode: input.sharingMode,
    })
    toast.success(t('library.saveFilters.saved'))
    if (input.sharingMode === 'shared') {
      setShareCollectionToken(detail.token)
    }
  }

  function handleShareDialogChange(open: boolean) {
    if (!open) {
      setShareCollectionToken(null)
      void collectionsList.refresh()
    }
  }

  function handleOpenCollection(token: string) {
    navigate(ROUTES.knowledgeCollection(token))
  }

  // A type, a facet or a search narrows the Library: the rail's All library is then not current.
  const isLibraryNarrowed =
    activeType != null ||
    searchQuery.trim() !== '' ||
    Object.values(filters).some((value) => value != null && value !== '')

  function handleClearLibraryFilters() {
    setSearchQuery('')
    setActiveType(null)
    setFilters({})
  }

  async function handleDelete(token: string) {
    setDeletingToken(token)
    try {
      await knowledgeApi.deleteDocument(token)
      setDocuments((prev) => prev.filter((doc) => doc.token !== token))
      dropFromSelection(token)
      toast.success(t('library.deleted'))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('library.deleteFailed')
      toast.error(message)
    } finally {
      setDeletingToken(null)
    }
  }

  function goToThreadsAsk(seed?: string, docToken?: string | null) {
    const params = new URLSearchParams()
    if (seed) params.set('q', seed)
    if (docToken) params.set('doc', docToken)
    const qs = params.toString()
    navigate(qs ? `/threads?${qs}` : '/threads')
  }

  function handleOpenDocument(token: string) {
    setOpenDocToken(token)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('doc', token)
      return next
    }, { replace: true })
  }

  function handleCloseDocument() {
    setOpenDocToken(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('doc')
      return next
    }, { replace: true })
  }

  function handleLibraryViewChange(view: string) {
    setSelectedItems([])
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(SOCIAL_UNIVERSE_PARAM)
      next.delete(SOCIAL_TRACKER_PARAM)
      if (view !== LIBRARY_AUDIENCES_VIEW) {
        for (const key of AUDIENCE_TAB_PARAMS) {
          next.delete(key)
        }
      }
      next.delete('creative')
      if (view !== 'creatives') next.delete('board')
      if (view === 'brands' || view === 'boards' || view === 'creatives') {
        next.set(LIBRARY_VIEW_PARAM, view)
      } else if (view === LIBRARY_SOCIAL_VIEW) {
        next.set(LIBRARY_VIEW_PARAM, LIBRARY_SOCIAL_VIEW)
      } else if (view === LIBRARY_AUDIENCES_VIEW) {
        next.set(LIBRARY_VIEW_PARAM, LIBRARY_AUDIENCES_VIEW)
      } else if (view === LIBRARY_CONNECTORS_VIEW) {
        next.set(LIBRARY_VIEW_PARAM, LIBRARY_CONNECTORS_VIEW)
      } else {
        next.delete(LIBRARY_VIEW_PARAM)
      }
      return next
    })
  }

  function handleOpenUniverseChange(token: string | null) {
    if (token) {
      navigate(ROUTES.knowledgeTopic(token))
    }
  }

  /** A saved Topic replaces its card; a new one opens its page at once (D22). */
  function handleTopicSaved(saved: SocialUniverseItem) {
    socialUniverses.replaceUniverse(saved)
    if (!editingUniverse) {
      navigate(ROUTES.knowledgeTopic(saved.token))
    }
  }

  function handleCreateTopic() {
    setEditingUniverse(null)
    setIsTopicDialogOpen(true)
  }

  function handleEditTopic(universe: SocialUniverseItem) {
    setEditingUniverse(universe)
    setIsTopicDialogOpen(true)
  }

  const legacyUniverseToken = searchParams.get(SOCIAL_UNIVERSE_PARAM)
  const legacyTrackerToken = searchParams.get(SOCIAL_TRACKER_PARAM)
  const isSocialListLoaded =
    !isAuthLoading && !socialUniverses.isLoading
  const legacyTrackerUniverseToken = legacyTrackerToken
    ? (socialUniverses.universes.find((universe) =>
        universe.channels.some(
          (channel) => channel.trackerToken === legacyTrackerToken,
        ),
      )?.token ?? null)
    : null

  useEffect(() => {
    if (legacyUniverseToken) {
      navigate(ROUTES.knowledgeTopic(legacyUniverseToken), { replace: true })
      return
    }
    if (!legacyTrackerToken || !isSocialListLoaded) {
      return
    }
    if (legacyTrackerUniverseToken) {
      navigate(ROUTES.knowledgeTopic(legacyTrackerUniverseToken), {
        replace: true,
      })
      return
    }
    // No Topic owns the channel any more: stay on the Social listening tab.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(SOCIAL_TRACKER_PARAM)
        return next
      },
      { replace: true },
    )
  }, [
    legacyUniverseToken,
    legacyTrackerToken,
    legacyTrackerUniverseToken,
    isSocialListLoaded,
    navigate,
    setSearchParams,
  ])

  const typeTabs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const doc of documents) {
      const label = documentTypeLabel(doc)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    const tabs = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([label, count]) => ({ key: label, label, count }))
    return [{ key: 'all', label: 'All', count: documents.length }, ...tabs]
  }, [documents])

  const ownerOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const doc of documents) {
      const name = doc.ownerName?.trim()
      if (!name) continue
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }))
  }, [documents])

  const facetFilters = useMemo(() => {
    const next: Record<string, string | null> = { ...filters }
    if (activeType) next.type = activeType
    else delete next.type
    return next
  }, [filters, activeType])

  const filteredDocuments = useMemo(() => {
    let list = documents.filter((d) =>
      documentMatchesFacetFilters(d, facetFilters),
    )
    const q = searchQuery.trim()
    if (q.length >= 2 && !isQuestionShaped(q) && searchHits.length > 0) {
      const hitTokens = new Set(searchHits.map((h) => h.token))
      list = list.filter((d) => hitTokens.has(d.token))
    } else if (
      q.length >= 2 &&
      !isQuestionShaped(q) &&
      !searching &&
      searchHits.length === 0
    ) {
      list = []
    }
    return list
  }, [documents, facetFilters, searchQuery, searchHits, searching])

  const hasActiveQueryOrFilters =
    (searchQuery.trim().length > 0 && !isQuestionShaped(searchQuery)) ||
    activeType != null ||
    Object.values(filters).some(Boolean)

  const showSearchResults =
    !isLibraryLocked &&
    searchQuery.trim().length >= 2 &&
    !isQuestionShaped(searchQuery)

  const showDocumentSkeleton = isAuthLoading || (canLoadLibrary && loading)

  const libraryResultCount = useMemo(() => {
    if (showSearchResults) {
      return searchHits.filter((hit) => {
        const doc = documents.find((d) => d.token === hit.token)
        if (!doc) {
          return true
        }
        return documentMatchesFacetFilters(doc, facetFilters)
      }).length
    }
    if (hasActiveQueryOrFilters) {
      return filteredDocuments.length
    }
    return null
  }, [
    showSearchResults,
    searchHits,
    documents,
    facetFilters,
    hasActiveQueryOrFilters,
    filteredDocuments.length,
  ])

  return (
    <Tabs
      value={activeLibraryView}
      onValueChange={handleLibraryViewChange}
      className="min-h-screen bg-background"
    >
      <PageLayout className="max-w-none gap-3 p-5 md:p-6">
        <KnowledgeLibraryHeader
          socialEnabled={isSocialEnabled}
          connectorsEnabled={connectorsEnabled}
          view={activeLibraryView}
          canLoadLibrary={canLoadLibrary}
          canManageTrackers={socialUniverses.canManage}
          onAddLinks={() => setAddLinksOpen(true)}
          onAddTracker={handleCreateTopic}
          onNewCollection={() => setCollectionDialog({ entry: 'header' })}
        >
          {isLibraryLocked || !isResourcesView || showSearchResults ? null : (
            <div
              className="flex min-w-0 flex-col gap-4 md:hidden"
              data-testid="library-home"
            >
              {/* The rail is the collections surface from md up; below it
                  (no rail) this grid carries them. */}
              <div className="md:hidden">
                <LibraryCollectionsGrid
                  collections={collectionsList.collections}
                  isLoading={isAuthLoading || collectionsList.isLoading}
                  hasError={collectionsList.hasError}
                  onNewCollection={() => setCollectionDialog({ entry: 'empty' })}
                />
              </div>
              {/* The Themes area (D14): the theme flow fills it in Phase B (B7). */}
              <section
                aria-label={t('library.collections.home.themes')}
                data-testid="library-themes-slot"
                hidden
              />
            </div>
          )}
        </KnowledgeLibraryHeader>
        <PageContent className="w-full gap-4">
          {creativeView ? <TabsContent value={creativeView} className="m-0 focus-visible:outline-none"><CreativeLibraryTab key={creativeView} view={creativeView} /></TabsContent> : isConnectorsView ? (
            <TabsContent
              value="connectors"
              className="m-0 focus-visible:outline-none"
            >
              <LibrarySources />
            </TabsContent>
          ) : isLibraryLocked ? (
            <LobbyEmptyState
              title={t('library.locked.title')}
              description={t('library.locked.body')}
            >
              <div data-testid="knowledge-locked-state">
                <Button asChild variant="primary">
                  <a
                    href={PYLON_CONTACT_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="knowledge-locked-contact-support"
                  >
                    {t('home.locked.contactSupport')}
                  </a>
                </Button>
              </div>
            </LobbyEmptyState>
          ) : (
            <>
              {isAudiencesView ? (
                <TabsContent
                  value="audiences"
                  className="m-0 focus-visible:outline-none"
                >
                  <AudiencesPage embedded listPath={ROUTES.knowledge} />
                </TabsContent>
              ) : isSocialView ? (
                <TabsContent
                  value="social"
                  className="m-0 focus-visible:outline-none"
                >
                  <div className="flex flex-col gap-4">
                    <SocialTopicListToolbar filters={socialList.filters} options={socialList.options}
                      resultCount={socialUniverses.isLoading || socialUniverses.hasError ? null : matchingUniverses.length}
                      totalCount={socialUniverses.universes.length} onFilterChange={socialList.setFilter}
                      onSearchChange={socialList.setSearch} onSortChange={socialList.setSort} onClear={socialList.clear} />
                    {socialList.hasFilters && socialUniverses.universes.length > 0 && matchingUniverses.length === 0 && !socialUniverses.isLoading && !socialUniverses.hasError ? (
                      <div className="flex flex-col items-start gap-2 rounded-xl border border-border p-6" data-testid="topic-list-no-results">
                        <Text>{t('socialListening.topicTab.noSearchResults')}</Text>
                        <Text size="sm" color="muted">{t('socialListening.topicTab.filters.emptyHint')}</Text>
                        <Button variant="outline" size="sm" onClick={socialList.clear}>{t('socialListening.topicTab.filters.clearAll')}</Button>
                      </div>
                    ) : <SocialMediaTab
                      universes={matchingUniverses}
                      canManage={socialUniverses.canManage}
                      syncSchedule={socialUniverses.schedule}
                      isLoading={socialUniverses.isLoading}
                      hasError={socialUniverses.hasError}
                      showHeader={false}
                      onOpenUniverseChange={handleOpenUniverseChange}
                      onUniverseChanged={socialUniverses.replaceUniverse}
                      onUniverseDeleted={socialUniverses.removeUniverse}
                      onRefresh={() => void socialUniverses.refresh()}
                      onCreateTopic={handleCreateTopic}
                      onEditTopic={handleEditTopic}
                      canAddToCollection={assistantPlatformEnabled}
                    />}
                  </div>
                </TabsContent>
              ) : (
                <TabsContent
                  value="docs"
                  aria-label={t('library.collections.resources.title')}
                  className="m-0 flex min-w-0 items-start gap-9 focus-visible:outline-none"
                >
                  {/* Option B: the collections rail beside the grid; below md
                      the collections grid above carries them instead. */}
                  <div className="sticky top-6 hidden md:block">
                    <CollectionsRail
                      collections={collectionsList.collections}
                      isLoading={isAuthLoading || collectionsList.isLoading}
                      hasError={collectionsList.hasError}
                      onSelect={handleOpenCollection}
                      onSelectAll={handleClearLibraryFilters}
                      onNewCollection={() => setCollectionDialog({ entry: 'header' })}
                      isLibraryNarrowed={isLibraryNarrowed}
                      storageScope={user?.id}
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <KnowledgeLibraryBand
                      typeTabs={typeTabs}
                      activeType={activeType}
                      onTypeChange={setActiveType}
                      facetCatalog={facetCatalog}
                      filters={filters}
                      onFilterChange={(kind, value) =>
                        setFilters((prev) => ({ ...prev, [kind]: value }))
                      }
                      showFilterPanel={showFilterPanel}
                      onToggleFilterPanel={() => setShowFilterPanel((v) => !v)}
                      resultCount={libraryResultCount}
                      ownerOptions={ownerOptions}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      filterActions={
                        <SaveFiltersPopover
                          filters={facetFilters}
                          facetCatalog={facetCatalog}
                          hasSearchQuery={searchQuery.trim().length > 0}
                          onSave={handleSaveFilters}
                        />
                      }
                      search={
                        <KnowledgeLibrarySearch
                          compact
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onAsk={goToThreadsAsk}
                        />
                      }
                    />

                    {showSearchResults ? (
                      <>
                        {!searching && searchHits.length === 0 ? null : (
                          <LibraryGroupedSearchResults
                            query={groupedQuery}
                            results={librarySearch.results}
                            isLoading={librarySearch.isLoading}
                            hasError={librarySearch.hasError}
                            onOpenBrand={handleOpenBrand}
                          />
                        )}
                        <KnowledgeSearchResults
                          query={searchQuery.trim()}
                          hits={searchHits.filter((h) => {
                            const doc = documents.find((d) => d.token === h.token)
                            if (!doc) return true
                            return documentMatchesFacetFilters(doc, facetFilters)
                          })}
                          searching={searching}
                          documents={documents}
                        />
                      </>
                    ) : (
                      <KnowledgeDocumentCollection
                        showDocumentSkeleton={showDocumentSkeleton}
                        filteredDocuments={filteredDocuments}
                        hasActiveQueryOrFilters={hasActiveQueryOrFilters}
                        viewMode={viewMode}
                        deletingToken={deletingToken}
                        onDelete={(token) => void handleDelete(token)}
                        onDocumentUpdated={handleDocumentUpdated}
                        onOpen={handleOpenDocument}
                        onAddLinks={() => setAddLinksOpen(true)}
                        onClearFilters={handleClearLibraryFilters}
                        selection={{ selectedTokens, onToggle: toggleSelected }}
                        lead={
                          viewMode === 'grid' && !hasActiveQueryOrFilters && pickFeaturedCollection(collectionsList.collections) ? (
                            <LibraryFeaturedBanner collections={collectionsList.collections} />
                          ) : null
                        }
                      />
                    )}
                    <LibrarySelectionBar
                      items={selectedItems}
                      onClear={() => setSelectedItems([])}
                      onKeepOnly={setSelectedItems}
                    />
                  </div>
                </TabsContent>
              )}
            </>
          )}
        </PageContent>
      </PageLayout>
      <SocialUniverseDialog
        open={isTopicDialogOpen}
        universe={editingUniverse}
        onOpenChange={setIsTopicDialogOpen}
        onSaved={handleTopicSaved}
      />
      <KnowledgeAddLinksModal
        open={addLinksOpen}
        onOpenChange={setAddLinksOpen}
        onDocumentsChanged={() => void loadLibrary({ quiet: true })}
      />
      {openDocToken ? (
        <KnowledgeDocumentModal
          documentToken={openDocToken}
          onClose={handleCloseDocument}
          onDocumentUpdated={handleDocumentUpdated}
          onDeleted={(token) => {
            setDocuments((prev) => prev.filter((doc) => doc.token !== token))
            dropFromSelection(token)
          }}
        />
      ) : null}
      <CollectionCreateDialog
        open={collectionDialog != null}
        onOpenChange={(open) => {
          if (!open) {
            setCollectionDialog(null)
          }
        }}
        mode="create"
        entry={collectionDialog?.entry}
        initialPreset={collectionDialog?.initialPreset}
        initialRules={collectionDialog?.initialRules}
        onSaved={handleCollectionSaved}
      />
      {shareAdapter ? (
        <CollectionShareDialog
          open={shareCollectionToken != null}
          onOpenChange={handleShareDialogChange}
          adapter={shareAdapter}
        />
      ) : null}
    </Tabs>
  )
}
